'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Profile } from '@/lib/types/auth.types';
import { authService } from '@/lib/services/auth.service';
import { get } from '@/lib/api/rest-client';

interface AuthContextType {
  isAuthenticated: boolean;
  profile: Profile | null;
  isLoading: boolean;
  // Add logout function if needed directly from context
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkInitialSession = async () => {
      setIsLoading(true);
      try {
        const hasSupabaseSession = await authService.isAuthenticated();
        setIsAuthenticated(hasSupabaseSession);

        if (hasSupabaseSession) {
          const {
            data: { user: supabaseUser },
          } = await authService.getSupabaseUser();
          if (supabaseUser) {
            try {
              // Fetch the detailed profile using the user ID
              const profileData = await get<{ data: Profile }>(`/profiles/${supabaseUser.id}`);
              // The actual profile is nested in profileData.data based on previous examples
              if (profileData && profileData.data) {
                setProfile(profileData.data);
                // Optionally, update the authService's internal currentUser if desired,
                // though authService methods like login already do this.
                // authService.setCurrentUser(profileData.data);
              } else {
                setProfile(null);
              }
            } catch (profileError) {
              console.error('Error fetching profile in AuthProvider:', profileError);
              setProfile(null);
              // Consider if a failed profile fetch for an authenticated user should log them out.
              // For now, it keeps them authenticated but without a profile.
              // If profile is critical, you might setIsAuthenticated(false) or even call authService.logout()
            }
          } else {
            setProfile(null); // No Supabase user implies no profile for the app
          }
        } else {
          setProfile(null); // Not authenticated, so no profile
        }
      } catch (error) {
        console.error('Error in checkInitialSession:', error);
        setIsAuthenticated(false); // Ensure authenticated is false on error
        setProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkInitialSession(); // Ensure this is called

    // Optional: Listen for storage events if login/logout can happen in other tabs
    const handleStorageChange = () => {
      checkInitialSession(); // Re-check session on storage change
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Removed authService from dependency array as it's a stable singleton

  const value = {
    isAuthenticated,
    profile,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
